import { Icon } from "armstrong-react";
import React from "react";
import { connect } from "react-redux";

import { Loader } from "../../../core/atoms";
import { MatchState } from "../../helpers/reduxHelpers";
import * as priceHelpers from "./../../../bothends/priceHelpers";
import { Tooltip } from "./../tooltip";
import { CurrencySelector } from "./currencySelector";
import { PriceInput } from "./priceInput";

import "./priceCalculator.scss";

interface OwnProps {
  insertPrice?: (formattedPrice: string) => void;
  onInsert?: () => void;
}

interface StateProps {
  userState: IState<IUser>;
  enquiryState: IState<IEnquiryOutput>;
  currencies: ICurrency[];
  customerCurrency: ICurrency;
  ltoCurrency: ICurrency;
}

type Props = OwnProps & StateProps;

/**
 * Component to let user convert a price between two currencies
 */
const PriceCalculatorComponent = (props: Props) => {

  const [ltoPrice, setLtoPrice] = React.useState<IPriceInCurrency>({
    price: 0,
    currency: props.ltoCurrency,
  });

  const [customerPrice, setCustomerPrice] = React.useState<IPriceInCurrency>({
    price: 0,
    currency: props.customerCurrency,
  });

  const { commissionRate, isConnected } = (props.enquiryState.data || {} as IEnquiryOutput);

  const onLtoPriceChange = React.useCallback((newPrice: IPriceInCurrency) => {
    const newCustomerPrice = priceHelpers.convertForCustomer(newPrice, customerPrice.currency, commissionRate);

    setLtoPrice(newPrice);
    setCustomerPrice(newCustomerPrice);
  }, [customerPrice, commissionRate]);

  const onCustomerPriceChange = React.useCallback((newPrice: IPriceInCurrency) => {
    const newLtoPrice = priceHelpers.convertForLto(newPrice, ltoPrice.currency, commissionRate);

    setLtoPrice(newLtoPrice);
    setCustomerPrice(newPrice);
  }, [ltoPrice, commissionRate]);

  // Admins can change both prices for preview
  // Could be useful to let LTOs work out prices in alternative customer currencies?
  const canChangeCurrency = (props.userState.data || {} as IUserSummary).userType === UserType.Administrator;
  const onCustomerCurrencyChange = React.useCallback(
    (newCurrency: ICurrency) => onCustomerPriceChange({ ...customerPrice, currency: newCurrency }), [customerPrice]);
  const onLtoCurrencyChange = React.useCallback(
    (newCurrency: ICurrency) => onLtoPriceChange({ ...ltoPrice, currency: newCurrency }), [ltoPrice]);

  const insertPrice = React.useCallback(() => {
    if (props.insertPrice) {
      const formattedPrice = priceHelpers.formatForCustomer(customerPrice);
      props.insertPrice(formattedPrice);
    }

    if (props.onInsert) {
      props.onInsert();
    }
  }, [customerPrice]);

  if (!isConnected) {
    // Unconnected enquiries are only shown to admins
    return (
      <div className="price-calculator price-calculator--disabled">
        <Icon icon={Icon.Icomoon.unlink2} />
        <p>Connect this enquiry to an LTO to use the price calculator.</p>
      </div>
    );
  }

  if (!props.customerCurrency || !props.ltoCurrency) {
    return (
      <div className="price-calculator price-calculator--disabled">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {MatchState(props.enquiryState, {
        data: enquiry => (
          <div className="price-calculator">
            <div className="price-calculator-section price-calculator-top">
              <div className="price-input-info">
                <div className="price-input-label">
                  Your price
                  <Tooltip text="You will receive (net)" textLength="small">
                    <Icon icon={Icon.Icomoon.question4} />
                  </Tooltip>
                </div>

                <CurrencySelector
                  selectedCurrency={ltoPrice.currency}
                  currencies={props.currencies}
                  onCurrencySelect={onLtoCurrencyChange}
                  canChangeCurrency={canChangeCurrency}
                />
              </div>
              <form action={null} onSubmit={insertPrice}>
                <PriceInput price={ltoPrice} onChange={onLtoPriceChange} />
              </form>
            </div>

            <div className="price-calculator-section price-calculator-bottom">
              <div className="price-input-info">
                <div className="price-input-label">
                  Sale price
                  <Tooltip text="Customer price (inc. TravelLocal commission)" textLength="small">
                    <Icon icon={Icon.Icomoon.question4} />
                  </Tooltip>
                </div>

                <CurrencySelector
                  selectedCurrency={customerPrice.currency}
                  currencies={props.currencies}
                  onCurrencySelect={onCustomerCurrencyChange}
                  canChangeCurrency={canChangeCurrency}
                />
              </div>
              <p className="price-input-sub-label">
                for {enquiry.customer.firstName} {enquiry.customer.lastName} <strong>{enquiry.reference}</strong>
              </p>

              <PriceInput price={customerPrice} onChange={onCustomerPriceChange} />
            </div>

            <div className="notes-and-actions">
              <button
                type="button"
                className="price-calculator__insert button primary-button text--regular text--accent"
                disabled={!customerPrice.price}
                onClick={insertPrice}>
                Insert
              </button>

              {enquiry.bookedTime != null &&
                <div className="price-note">
                  The exchange rate for this enquiry was fixed at the time of booking.
                </div>
              }
            </div>
          </div>
        ),
      })}
    </>
  );
};

const stateToProps = (state: IAppStoreState): StateProps => {
  const { enquiry: enquiryState } = state;
  const enquiry = (enquiryState.data || {}) as IConnectedEnquiryOutput;

  // Exchange rates get fixed at the point of booking
  // + enquiryItineraryCurrencies if not null contains the fixed currencies
  const isFixed = enquiry?.bookedTime != null;
  const { currency: fromCurrency } = enquiry;
  const { currency: toCurrency } = enquiry.lto || {} as ILtoSummary;
  const { customerCurrency: fixedFromCurrency, ltoCurrency: fixedToCurrency }
    = state.enquiryItineraryCurrencies.data || {} as IEnquiryItineraryCurrencies;

  const filteredCurrencies = (state.currencies.data || []).filter(x => x.availableToCustomers);

  return {
    enquiryState: state.enquiry,
    userState: state.user,
    currencies: filteredCurrencies,
    customerCurrency: isFixed ? fixedFromCurrency : fromCurrency,
    ltoCurrency: isFixed ? fixedToCurrency : toCurrency,
  };
};

export const PriceCalculator = connect<StateProps, void, OwnProps>(stateToProps)(PriceCalculatorComponent);
